import { useAuthContext } from '@phx/auth/client';
import {
    Button,
    Cover,
    Flex,
    Heading1,
    Stack,
    TextButton,
} from '@phx/design-system';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getFragment } from '../../graphql';
import type { ProviderOffersInfoFragment } from '../../graphql/generated/graphql';
import { usePatientContext } from '../../hooks/use-patient-context';
import {
    sortOffersByDeliveryTime,
    sortOffersByDistance,
    sortOffersByPlanPays,
    sortOffersByPrice,
} from '../../util/offers/sort-offers';
import { ConditionalComponent } from '../utility/ConditionalComponent';

import FilterCheckboxGroup from './FilterCheckboxGroup';
import FilterRadioGroup from './FilterRadioGroup';

type FilterCoverProps = {
    isOpen: boolean;
    onClose: () => void;
    pickUp?: boolean;
    providerOffers: ProviderOffersInfoFragment[];
    onSortedOffersChange?: (sortedOffers: ProviderOffersInfoFragment[]) => void;
    onActiveFiltersChange?: (activeFiltersCount: number) => void;
};

const sortOptions = {
    distance: 'distance',
    price: 'price',
    planPays: 'planPays',
    deliveryTime: 'deliveryTime',
};

export const FilterCover = ({
    isOpen,
    onClose,
    pickUp,
    providerOffers,
    onSortedOffersChange,
    onActiveFiltersChange,
}: FilterCoverProps) => {
    const { t } = useTranslation();

    const { isAuthenticated } = useAuthContext();
    const { patient } = usePatientContext();
    const { preferences } = usePatientContext();
    const { favoritePharmacies } = preferences;
    const { checkIsFavorite } = favoritePharmacies;

    const [savedFilters, setSavedFilters] = useState({
        withInsurance: false,
        smartPrice: false,
        favorite: false,
        sortBy: pickUp ? sortOptions.distance : sortOptions.price,
    });

    const [currentFilters, setCurrentFilters] = useState(savedFilters);

    const title = pickUp
        ? t('providerOffers.filter.pickUp')
        : t('providerOffers.filter.delivery');

    const coverages = patient?.displayableCoverages ?? [];

    const hasPhxBenefitsPlan =
        coverages.filter(
            (coverage) => coverage.__typename === 'PhxBenefitsPlan'
        ).length > 0;

    const sortByOptions = useMemo(() => {
        if (pickUp) {
            return [
                {
                    value: sortOptions.distance,
                    label: t('filterAndSort.radio.distance'),
                },
                {
                    value: sortOptions.price,
                    label: t('filterAndSort.radio.price'),
                },
                {
                    value: sortOptions.planPays,
                    label: t('filterAndSort.radio.planPays'),
                },
            ];
        }

        return [
            { value: sortOptions.price, label: t('filterAndSort.radio.price') },
            {
                value: sortOptions.deliveryTime,
                label: t('filterAndSort.radio.deliveryTime'),
            },
            {
                value: sortOptions.planPays,
                label: t('filterAndSort.radio.planPays'),
            },
        ];
    }, [hasPhxBenefitsPlan, isAuthenticated, pickUp]);

    useEffect(() => {
        if (!isOpen) {
            // Reset temporary filters to previously applied filters when the cover is closed without calling handleSubmit
            setCurrentFilters(savedFilters);
        }
    }, [isOpen, savedFilters]);

    const handleReset = useCallback(() => {
        setCurrentFilters({
            withInsurance: false,
            smartPrice: false,
            favorite: false,
            sortBy: sortByOptions[0].value,
        });
    }, []);

    //Sorting it client side to improve responsiveness of the dynamic button
    const sortedProviderOffers = useMemo(() => {
        let filteredOffers = providerOffers;

        // Filter by pricing options
        if (currentFilters.withInsurance && !currentFilters.smartPrice) {
            filteredOffers = filteredOffers.filter((offer) =>
                offer.offers.some((o) => o.__typename === 'InsuranceOffer')
            );
        } else if (currentFilters.smartPrice && !currentFilters.withInsurance) {
            filteredOffers = filteredOffers.filter((offer) =>
                offer.offers.some((o) => o.__typename === 'CashOffer')
            );
        } else if (currentFilters.smartPrice && currentFilters.withInsurance) {
            filteredOffers = filteredOffers.filter((offer) => {
                return (
                    offer.offers.some((o) => o.__typename === 'CashOffer') &&
                    offer.offers.some((o) => o.__typename === 'InsuranceOffer')
                );
            });
        }

        if (currentFilters.favorite) {
            return filteredOffers.filter((offer) => {
                const provider = getFragment(offer.provider);
                checkIsFavorite(provider.id);
            });
        }

        switch (currentFilters.sortBy) {
            case sortOptions.distance:
                return sortOffersByDistance(filteredOffers);
            case sortOptions.deliveryTime:
                return sortOffersByDeliveryTime(filteredOffers);
            case sortOptions.planPays:
                return sortOffersByPlanPays(filteredOffers);
            case sortOptions.price:
                return sortOffersByPrice(filteredOffers);
            default:
                return filteredOffers;
        }
    }, [providerOffers, currentFilters, checkIsFavorite]);

    const activeFilterCount = useMemo(() => {
        let count = 0;

        if (currentFilters.sortBy !== sortByOptions[0].value) count++;
        if (currentFilters.withInsurance || currentFilters.smartPrice) count++;
        if (currentFilters.favorite) count++;

        return count;
    }, [currentFilters, sortByOptions]);

    const handleSubmit = useCallback(() => {
        setSavedFilters(currentFilters);
        if (onSortedOffersChange) {
            onSortedOffersChange(sortedProviderOffers);
        }
        if (onActiveFiltersChange) {
            onActiveFiltersChange(activeFilterCount);
        }
        onClose();
    }, [
        currentFilters,
        sortedProviderOffers,
        onSortedOffersChange,
        onActiveFiltersChange,
        activeFilterCount,
    ]);

    const toggleWithInsurance = () =>
        setCurrentFilters((prev) => ({
            ...prev,
            withInsurance: !prev.withInsurance,
        }));
    const toggleSmartPrice = () =>
        setCurrentFilters((prev) => ({
            ...prev,
            smartPrice: !prev.smartPrice,
        }));
    const toggleFavorite = () =>
        setCurrentFilters((prev) => ({ ...prev, favorite: !prev.favorite }));

    return (
        <Cover visible={isOpen}>
            <Stack flex="1">
                <Flex justify="space-between" align="center">
                    <Heading1>
                        {`${title} ${t('providerOffers.filter.filters')}`}
                    </Heading1>
                    <TextButton
                        disabled={activeFilterCount === 0}
                        onClick={handleReset}
                    >
                        {t('filterAndSort.reset')}
                    </TextButton>
                </Flex>
                <Stack gap="xxl">
                    <FilterRadioGroup
                        title={t('filterAndSort.subtitle.sort')}
                        options={sortByOptions}
                        selection={currentFilters.sortBy}
                        onChange={(value) =>
                            setCurrentFilters((prev) => ({
                                ...prev,
                                sortBy: value,
                            }))
                        }
                    />
                    <ConditionalComponent condition={isAuthenticated}>
                        <FilterCheckboxGroup
                            withInsurance={currentFilters.withInsurance}
                            toggleWithInsurance={toggleWithInsurance}
                            smartPrice={currentFilters.smartPrice}
                            toggleSmartPrice={toggleSmartPrice}
                            favorite={currentFilters.favorite}
                            toggleFavorite={toggleFavorite}
                            hasPhxBenefitsPlan={hasPhxBenefitsPlan}
                        />
                    </ConditionalComponent>
                </Stack>
                <Button onClick={handleSubmit}>
                    {sortedProviderOffers.length
                        ? t('common.cover.setFilters', {
                              count: sortedProviderOffers.length,
                          })
                        : t('common.cover.noPharmaciesFound')}
                </Button>
            </Stack>
        </Cover>
    );
};
